import React, { useContext, useEffect, useState } from 'react';
import { SiteContext } from '../../../context/SiteContext';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { ButtonBack } from '../../common/Button/ButtonBack';
import {
  CoverDetailsDropDowns,
  InsuranceJourneyContext,
  SelectOpt
} from '../../common/CommonTypes';
import SelectInput, {
  SelectInputHelper
} from '../../common/SelectInput/SelectInput';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import RadioOptions from '../../common/RadioOptions/RadioOptions';
import {
  InsuranceJourneyHelperText,
  InsuranceSaveForLaterMessage,
  PageAbsolutePath
} from '../../../consts/Common';
import {
  populateSelectOptions,
  setCountryCode
} from '../../common/HelperMethods';
import { ButtonCommand } from '../../common/Button/ButtonCommand';
import ClickableHelpText from '../../common/HelpText/ClickableHelpText';
import SimpleDateInput from '../../common/DateInput/SimpleDateInput';
import { CarUses, PagesWithDropdown } from '../../common/Constants';
import useCarInsuranceController from '../../../hooks/insuranceJourney/useCarInsuranceController';
import LoadingContainer from '../../common/LoadingContainer/LoadingContainer';
import { useErrorBoundary } from 'react-error-boundary';

type CoverDetailsErrors = {
  estimatedMileage: string;
  carUses: string;
  coverStartDate: string;
  coverStartTime: string;
  registeredVehicleOwner: string;
  registeredVehicleKeeper: string;
  paymentMode: string;
  estimatedBusinessMileage: string;
};

const initialFormErrors: CoverDetailsErrors = {
  estimatedMileage: '',
  carUses: '',
  coverStartDate: '',
  coverStartTime: '',
  registeredVehicleOwner: '',
  registeredVehicleKeeper: '',
  paymentMode: '',
  estimatedBusinessMileage: ''
};

export const InsuranceCoverDetailsPage = (): JSX.Element => {
  const navigate = useNavigate();
  const { showBoundary } = useErrorBoundary();
  const { journey, pageEvents } = useOutletContext<InsuranceJourneyContext>();
  const { coverDetails, staticData, savingQuote, pageLoading } = journey;
  const { coverDetailsPage, commonEvents } = pageEvents;
  const { onPageChange, setPageLoading } = commonEvents;
  const { site } = useContext(SiteContext);
  const token = site.user.token;

  const {
    estimatedMileage,
    carUses,
    coverStartDate,
    coverStartTime,
    registeredVehicleKeeper,
    registeredVehicleOwner,
    paymentMode,
    estimatedBusinessMileage
  } = coverDetails;

  const {
    onChangeCarUse,
    onChangeCoverStartDate,
    onChangeCoverStartTime,
    onChangeEstimatedBusinessMileage,
    onChangeEstimatedMileage,
    onChangePaymentMode,
    onChangeRegisteredVehicleKeeper,
    onChangeRegisteredVehicleOwner
  } = coverDetailsPage;

  const { setSavingQuote, setStaticData } = commonEvents;

  const [showEstimatedBusinnessMileage, setShowEstimatedBusinnessMileage] =
    useState(false);
  const [estimatedMileagesOptions, setEstimatedMileagesOptions] = useState<
    SelectOpt[]
  >([]);
  const [
    estimatedBusinessMileagesOptions,
    setEstimatedBusinessMileagesOptions
  ] = useState<SelectOpt[]>([]);
  const [carUsesOptions, setCarUsesOptions] = useState<SelectOpt[]>([]);
  const [registeredVehicleKeeperOptions, setRegisteredVehicleKeeperOptions] =
    useState<SelectOpt[]>([]);
  const [registeredVehicleOwnerOptions, setRegisteredVehicleOwnerOptions] =
    useState<SelectOpt[]>([]);

  const { getCoverDetailsPageDropdownValues } = useCarInsuranceController();

  const paymentModeList = ['Monthly', 'Annually'];
  const [formErrors, setFormErrors] =
    useState<CoverDetailsErrors>(initialFormErrors);

  const [coverStartTimeOptions, setCoverStartTimeOptions] = useState<
    SelectOpt[]
  >([]);

  const today = new Date();
  const maxStartDate = new Date();
  maxStartDate.setDate(maxStartDate.getDate() + 30);

  const onBackendFailure = (err: string) => {
    showBoundary(new Error(err));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const dropDownValuesForPage = staticData?.find(
      (d) => d.page === PagesWithDropdown.InsuranceCoverDetailsPage
    );

    !dropDownValuesForPage &&
      getCoverDetailsPageDropdownValues(
        token,
        getCoverDetailsPageDropdownValuesSuccess,
        onBackendFailure
      );

    getCoverStartTimeOptions();
  }, []);

  const getUtcDate = (stringDate: string) => {
    const date = new Date(stringDate);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const utcDate = new Date(Date.UTC(year, month, day, 0, 0, 0));

    return utcDate;
  };

  useEffect(() => {
    coverStartDate
      ? onChangeCoverStartTime('')
      : onChangeCoverStartTime(coverStartTime);
    const date =
      typeof coverStartDate === 'string'
        ? getUtcDate(coverStartDate)
        : coverStartDate;
    if (date?.toDateString() === new Date().toDateString())
      getCoverStartTimeOptions(new Date().getHours() + 1);
    else getCoverStartTimeOptions();
  }, [coverStartDate]);

  useEffect(() => {
    onChangeCoverStartTime(coverStartTime);
  }, [coverStartTime]);

  useEffect(() => {
    const estimatedMileagesOptionValues = staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.InsuranceCoverDetailsPage &&
        d.controlName === 'estimated-mileages'
    )?.values;

    const carUsesOptionValues = staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.InsuranceCoverDetailsPage &&
        d.controlName === 'car-uses'
    )?.values;

    const vehicelOwnersOptions = staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.InsuranceCoverDetailsPage &&
        d.controlName === 'vehicle-owners'
    )?.values;

    if (estimatedMileagesOptionValues) {
      setEstimatedMileagesOptions(estimatedMileagesOptionValues);
      setEstimatedBusinessMileagesOptions(estimatedMileagesOptionValues);
    } else {
      setEstimatedMileagesOptions([]);
      setEstimatedBusinessMileagesOptions([]);
    }

    carUsesOptionValues
      ? setCarUsesOptions(carUsesOptionValues)
      : setCarUsesOptions([]);

    if (vehicelOwnersOptions) {
      setRegisteredVehicleKeeperOptions(vehicelOwnersOptions);
      setRegisteredVehicleOwnerOptions(vehicelOwnersOptions);
    } else {
      setRegisteredVehicleKeeperOptions([]);
      setRegisteredVehicleOwnerOptions([]);
    }
  }, [staticData]);

  useEffect(() => {
    ShowEstimatedBuisnessMileage();
  }, [carUses]);

  const getCoverDetailsPageDropdownValuesSuccess = (
    dropdownValues: CoverDetailsDropDowns
  ) => {
    const newValues = [
      {
        page: PagesWithDropdown.InsuranceCoverDetailsPage,
        controlName: 'estimated-mileages',
        values: populateSelectOptions(dropdownValues.estimatedMileages)
      },
      {
        page: PagesWithDropdown.InsuranceCoverDetailsPage,
        controlName: 'car-uses',
        values: populateSelectOptions(dropdownValues.carUses)
      },
      {
        page: PagesWithDropdown.InsuranceCoverDetailsPage,
        controlName: 'vehicle-owners',
        values: populateSelectOptions(dropdownValues.vehicleOwners)
      }
    ];

    setStaticData(staticData ? staticData.concat(...newValues) : newValues);
  };

  const getCoverStartTimeOptions = (startFrom?: number) => {
    const options = [];

    for (let i = startFrom ?? 0; i <= 23; i++) {
      const timeValue = i < 10 ? '0' + i + ':00' : i + ':00';
      const item: SelectOpt = {
        id: 'T' + i,
        value: timeValue,
        text: timeValue,
        disabled: false
      };
      options.push(item);
    }

    setCoverStartTimeOptions(options);
  };

  const validateCoverDetails = (): boolean => {
    let isValid = true;
    const updatedFormErrors = { ...initialFormErrors };

    if (
      !SelectInputHelper.isValidValue(carUses) ||
      CommonValidation.isEmpty(coverStartDate)
    ) {
      isValid = false;
      updatedFormErrors.carUses = 'Car Use is required';
    }
    if (
      !SelectInputHelper.isValidValue(estimatedMileage) ||
      estimatedMileage == ''
    ) {
      isValid = false;
      updatedFormErrors.estimatedMileage = 'Estimated Mileage is required';
    }
    if (
      CommonValidation.isDateNull(coverStartDate, true) ||
      CommonValidation.isEmpty(coverStartDate)
    ) {
      isValid = false;
      updatedFormErrors.coverStartDate = 'Cover start date is required';
    } else if (!CommonValidation.isValidDate(coverStartDate, false)) {
      isValid = false;
      updatedFormErrors.coverStartDate = 'Cover start date is invalid';
    }
    if (!SelectInputHelper.isValidValue(coverStartTime)) {
      isValid = false;
      updatedFormErrors.coverStartTime = 'Cover start time is required';
    }
    if (
      !SelectInputHelper.isValidValue(registeredVehicleKeeper) ||
      CommonValidation.isEmpty(registeredVehicleKeeper)
    ) {
      isValid = false;
      updatedFormErrors.registeredVehicleKeeper =
        'Registered vehicle keeper is required';
    }
    if (
      !SelectInputHelper.isValidValue(registeredVehicleOwner) ||
      CommonValidation.isEmpty(registeredVehicleOwner)
    ) {
      isValid = false;
      updatedFormErrors.registeredVehicleOwner =
        'Registered vehicle owner is required';
    }
    if (
      !SelectInputHelper.isValidValue(paymentMode) ||
      CommonValidation.isEmpty(paymentMode)
    ) {
      isValid = false;
      updatedFormErrors.paymentMode = 'Pay for car insurance is required';
    }
    setFormErrors(updatedFormErrors);

    return isValid;
  };

  const ShowEstimatedBuisnessMileage = (): void => {
    switch (carUses) {
      case CarUses.SDandP_Business_use_policy_holder_class_1:
      case CarUses.SDandP_Business_use_policy_holder_employer_or_name_driver:
      case CarUses.SDandP_Business_use_policy_holder_spouse_class_1: {
        setShowEstimatedBusinnessMileage(true);
        break;
      }
      default: {
        setShowEstimatedBusinnessMileage(false);
      }
    }
  };

  const onBackClick = () => {
    const path: string = setCountryCode(
      PageAbsolutePath.Insurance.VehicleDetails,
      site.countryCode
    );
    navigate(path);
  };

  const onNextClick = () => {
    const isValid = validateCoverDetails();

    if (isValid) {
      const path: string = setCountryCode(
        PageAbsolutePath.Insurance.AddressDetailsPage,
        site.countryCode
      );
      navigate(path);
      onPageChange(PageAbsolutePath.Insurance.InsuranceCoverDetailsPage, true);
    }
  };

  const onChangeCoverStartDateValue = (date: Date | null): void => {
    onChangeCoverStartDate(date === null ? undefined : date);
  };

  const onSaveLaterClick = (): void => {
    setPageLoading(true);
    setSavingQuote(true);
  };

  return (
    <LoadingContainer
      loading={pageLoading}
      message={InsuranceSaveForLaterMessage}>
      <>
        <div>
          <div className='one-col'>
            <ButtonCommand
              name='btn-back-top'
              text={'< Back'}
              onClick={onBackClick}
            />
          </div>
          <h4 className='section-header m1-top'>Cover Details</h4>
          <div className='table-flex-layout'>
            <div className='form-container'>
              <div className='one-col'>
                <div className='control-label help-text'>
                  What’s the estimated annual mileage?
                  <ClickableHelpText
                    helpText={
                      InsuranceJourneyHelperText.Pages.CoverDetails
                        .AnnualMileage
                    }
                  />
                </div>
                <div className='control'>
                  <SelectInput
                    name='estimatedMileage'
                    className='input-text w250'
                    value={estimatedMileage}
                    onChange={onChangeEstimatedMileage}
                    options={estimatedMileagesOptions}
                    forceDefault
                    addDefaultOption
                    error={formErrors.estimatedMileage}
                  />
                </div>
              </div>
              <div className='one-col'>
                <div className='control-label help-text'>
                  What will you be using the car for?
                  <ClickableHelpText
                    helpText={
                      InsuranceJourneyHelperText.Pages.CoverDetails.CarUse
                    }
                  />
                </div>
                <div className='control'>
                  <SelectInput
                    name='carUses'
                    className='input-text w250'
                    value={carUses}
                    onChange={onChangeCarUse}
                    options={carUsesOptions}
                    forceDefault
                    addDefaultOption
                    error={formErrors.carUses}
                  />
                </div>
              </div>
              {showEstimatedBusinnessMileage && (
                <div className='one-col'>
                  <div className='control-label'>
                    What is the estimated annual business mileage?
                  </div>
                  <div className='control'>
                    <SelectInput
                      name='estimatedBusinessMileage'
                      className='input-text w250'
                      value={estimatedBusinessMileage}
                      onChange={onChangeEstimatedBusinessMileage}
                      options={estimatedBusinessMileagesOptions}
                      forceDefault
                      addDefaultOption
                      error={formErrors.estimatedBusinessMileage}
                    />
                  </div>
                </div>
              )}
              <div className='one-col'>
                <div className='control-label'>
                  When would you like your Annual Cover to start within the next
                  30 days?
                </div>
                <div className='control'>
                  <SimpleDateInput
                    name='coverStart-date'
                    value={coverStartDate}
                    error={formErrors.coverStartDate}
                    onDateChange={onChangeCoverStartDateValue}
                    minimumDate={today}
                    maximumDate={maxStartDate}
                  />
                </div>
              </div>
              <div className='one-col'>
                <div className='control-label help-text'>
                  Cover start time ( Hour - Minute )
                  <ClickableHelpText
                    helpText={
                      InsuranceJourneyHelperText.Pages.CoverDetails
                        .CoverStartTime
                    }
                  />
                </div>
                <div className='control'>
                  <SelectInput
                    name='coverStartTime'
                    value={coverStartTime}
                    options={coverStartTimeOptions}
                    onChange={onChangeCoverStartTime}
                    forceDefault
                    addDefaultOption
                    error={formErrors.coverStartTime}
                  />
                </div>
              </div>
              <div className='one-col'>
                <div className='control-label help-text'>
                  Who is the registered vehicle keeper?
                  <ClickableHelpText
                    helpText={
                      InsuranceJourneyHelperText.Pages.CoverDetails
                        .RegisteredKeeper
                    }
                  />
                </div>
                <div className='control'>
                  <SelectInput
                    name='registeredVehicleKeeper'
                    value={registeredVehicleKeeper}
                    options={registeredVehicleKeeperOptions}
                    onChange={onChangeRegisteredVehicleKeeper}
                    forceDefault
                    addDefaultOption
                    error={formErrors.registeredVehicleKeeper}
                  />
                </div>
              </div>
              <div className='one-col'>
                <div className='control-label help-text'>
                  Who is the registered vehicle owner?
                  <ClickableHelpText
                    helpText={
                      InsuranceJourneyHelperText.Pages.CoverDetails
                        .RegisteredOwner
                    }
                  />
                </div>
                <div className='control'>
                  <SelectInput
                    name='registeredVehicleOwner'
                    value={registeredVehicleOwner}
                    options={registeredVehicleOwnerOptions}
                    onChange={onChangeRegisteredVehicleOwner}
                    forceDefault
                    addDefaultOption
                    error={formErrors.registeredVehicleOwner}
                  />
                </div>
              </div>
              <div className='one-col'>
                <RadioOptions
                  errorMessage={formErrors.paymentMode}
                  selectedOption={paymentMode}
                  optionsList={paymentModeList}
                  setSelectedOption={onChangePaymentMode}
                  label='How do you usually pay for your car insurance?'
                  name='paymentMode'
                />
              </div>
            </div>
          </div>
          <div className='flex-row form-nav'>
            <ButtonBack
              name='btn-back'
              text='Back'
              onClick={onBackClick}
              additionalClassNames='btn-back'
            />
            <ButtonContinue
              name='btn-save-later'
              text='Save For Later'
              onClick={onSaveLaterClick}
              additionalClassNames='btn-save'
              disabled={savingQuote}
            />
            <ButtonContinue
              name='btn-next'
              text='Next'
              onClick={onNextClick}
              additionalClassNames='btn-continue'
            />
          </div>
        </div>
      </>
    </LoadingContainer>
  );
};

export default InsuranceCoverDetailsPage;
